import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, vShow as _vShow, withDirectives as _withDirectives, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-09fd6640"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["id", "aria-labelledby", "aria-hidden"]
const _hoisted_2 = ["id"]
const _hoisted_3 = {
  type: "button",
  class: "btn-close",
  "data-bs-dismiss": "modal",
  "aria-label": "Close"
}
const _hoisted_4 = {
  type: "button",
  class: "btn btn-secondary",
  "data-bs-dismiss": "modal"
}

import { assertIsDefined } from "@/utils/asserts";
import {
  ref,
  onMounted,
  onBeforeUnmount,
  computed,
  PropType,
  onUnmounted,
} from "vue";
import { useI18n } from "vue-i18n";
import { Modal } from "bootstrap";
import { allClear } from "../AllClearService";

export interface ModalInterface {
  /**
   * Manually readjust the modal’s position if the height of a modal changes while it is open (i.e. in case a scrollbar appears).
   */
  handleUpdate(): void;
  /**
   * Manually toggles a modal. Returns to the caller before the modal has actually been shown or hidden (i.e. before the shown.bs.modal or hidden.bs.modal event occurs).
   */
  toggle(relatedTarget?: HTMLElement): void;
  /**
   * Manually opens a modal. Returns to the caller before the modal has actually been shown (i.e. before the shown.bs.modal event occurs).
   */
  show(relatedTarget?: HTMLElement): void;
  /**
   * Manually hides a modal. Returns to the caller before the modal has actually been hidden (i.e. before the hidden.bs.modal event occurs).
   */
  hide(): void;
  /**
   * 開いてから 閉じるまでの promise を返す
   * @param relatedTarget
   * @param signal
   */
  showAsync(
    relatedTarget?: HTMLElement,
    signal?: AbortSignal
  ): Promise<unknown[]>;
  shownPromise(config?: { signal: AbortSignal }): Promise<void>;
  /**
   * 現在表示しているかどうか 表示していれば `true` それ以外なら `false`
   */
  isShow: boolean;
}
export type ModalIntrface = ModalInterface;

export default /*#__PURE__*/_defineComponent({
  props: {
  id: {
    required: true,
    type: String,
  },
  title: {
    require: false,
    type: String,
    default: null,
  },
  disableClose: {
    require: false,
    type: Boolean,
    default: false,
  },
  class: {
    require: false,
    type: [String, Array, Object],
    default: "fade",
  },
  modalClass: {
    require: false,
    type: [String, Array, Object],
  },
  contentClass: {
    require: false,
    type: [String, Array, Object],
  },
  headerClass: {
    require: false,
    type: [String, Array, Object],
  },
  bodyClass: {
    require: false,
    type: [String, Array, Object],
  },
  footerClass: {
    require: false,
    type: [String, Array, Object],
  },
  isShow: {
    require: false,
    type: Boolean,
    default: null,
  },
  show: {
    require: false,
    type: Function,
    default: null,
  },
  shown: {
    require: false,
    type: Function,
    default: null,
  },
  hide: {
    require: false,
    type: Function,
    default: null,
  },
  hidden: {
    require: false,
    type: Function,
    default: null,
  },
  hidePrevented: {
    require: false,
    type: Function,
    default: null,
  },
  options: {
    type: Object as PropType<Partial<Modal.Options>>,
    require: false,
    default: undefined,
  },
  autofocus: {
    type: Boolean,
    default: true,
  },
  /**
   * 強制的に閉じるのを抑制する
   */
  ignoreForceClear: {
    type: Boolean,
    require: false,
    default: false,
  },
},
  emits: ["show", "shown", "hide", "hidden", "hidePrevented"],
  setup(__props, { expose, emit }) {

const props = __props

const { t } = useI18n();
// 公開プロパティ設定

// 公開イベント設定

const defines: ModalIntrface = new (class implements ModalIntrface {
  /**
   * Manually readjust the modal’s position if the height of a modal changes while it is open (i.e. in case a scrollbar appears).
   */
  handleUpdate(): void {
    const bm = bootstrapModal.value;
    assertIsDefined(bm);
    bm.handleUpdate();
  }
  /**
   * Manually toggles a modal. Returns to the caller before the modal has actually been shown or hidden (i.e. before the shown.bs.modal or hidden.bs.modal event occurs).
   */
  toggle(relatedTarget?: HTMLElement): void {
    const bm = bootstrapModal.value;
    assertIsDefined(bm);
    bm.toggle(relatedTarget);
  }
  /**
   * Manually opens a modal. Returns to the caller before the modal has actually been shown (i.e. before the shown.bs.modal event occurs).
   */
  show(relatedTarget?: HTMLElement): void {
    const bm = bootstrapModal.value;
    assertIsDefined(bm);
    bm.show(relatedTarget);
  }
  /**
   * 開いてから 閉じるまでの promise を返す
   * @param relatedTarget
   * @param signal
   */
  async showAsync(
    relatedTarget?: HTMLElement,
    signal?: AbortSignal
  ): Promise<unknown[]> {
    const once = true;
    const m = modal.value;
    assertIsDefined(m);
    const bm = bootstrapModal.value;
    assertIsDefined(bm);
    if (signal?.aborted ?? false) return Promise.reject();
    let rejectMethod: null | (() => void) = null;
    try {
      return await new Promise<unknown[]>((resolve, reject) => {
        if (signal?.aborted ?? false) {
          reject();
          return;
        }
        m.addEventListener(
          hiddenEventType,
          (...args: unknown[]) => {
            resolve(args);
          },
          {
            once,
            signal,
          }
        );
        rejectMethod = reject;
        signal?.addEventListener("abort", reject);
        bm.show(relatedTarget);
      });
    } finally {
      if (rejectMethod) signal?.removeEventListener("abort", rejectMethod);
    }
  }
  /**
   * Manually hides a modal. Returns to the caller before the modal has actually been hidden (i.e. before the hidden.bs.modal event occurs).
   */
  hide(): void {
    const m = bootstrapModal.value;
    assertIsDefined(m);
    m.hide();
  }
  get isShow(): boolean {
    return isShow.value;
  }
  async shownPromise(config?: { signal: AbortSignal }): Promise<void> {
    const signal = config?.signal;
    let rejectMethod: (() => void) | undefined = undefined;
    try {
      const m = modal.value;
      assertIsDefined(m);
      if (signal?.aborted) throw new DOMException("abort", "AbortError");
      await new Promise<void>((resolve, reject) => {
        if (signal) {
          if (signal.aborted) {
            reject(new DOMException("abort", "AbortError"));
          }
          signal.addEventListener("abort", reject);
          rejectMethod = reject;
        }
        m.addEventListener(shownEventType, () => resolve(), {
          once: true,
          signal,
        });
      });
    } finally {
      if (rejectMethod && signal)
        signal.removeEventListener("abort", rejectMethod);
    }
  }
})();
// 公開関数設定
expose(defines);
// 初回投入時のみ設定可能プロパティ群
const id = ref(props.id);
const title = computed(() => {
  const defaultTitle = t("MODAL.DEFAULT_TITLE");
  const propTitle = props.title;
  return propTitle ?? defaultTitle;
});
const isShow = ref(false);
const isFirstShow = ref(props.isShow ?? false);
const modal = ref<HTMLElement>();
const bootstrapModal = ref<Modal>();
const showEventType = "show.bs.modal";
const shownEventType = "shown.bs.modal";
const hiddenEventType = "hidden.bs.modal";
const hideEventType = "hide.bs.modal";
const hidePreventedEventType = "hidePrevented.bs.modal";
onMounted(() => {
  const m = modal.value;
  assertIsDefined(m);
  bootstrapModal.value = new Modal(m, props.options);
  m.addEventListener(showEventType, showHandler);
  m.addEventListener(shownEventType, shownHandler);
  m.addEventListener(hiddenEventType, hiddenHandler);
  m.addEventListener(hideEventType, hideHandler);
  m.addEventListener(hidePreventedEventType, hidePreventedHandler);
});
onBeforeUnmount(() => {
  const m = modal.value;
  assertIsDefined(m);
  m.removeEventListener(showEventType, showHandler);
  m.removeEventListener(shownEventType, shownHandler);
  m.removeEventListener(hiddenEventType, hiddenHandler);
  m.removeEventListener(hideEventType, hideHandler);
  m.removeEventListener(hidePreventedEventType, hidePreventedHandler);
  const bm = bootstrapModal.value;
  assertIsDefined(bm);
  bm.dispose();
});
function shownHandler() {
  if (props.autofocus && modal.value) {
    const m = modal.value;
    const e = m.querySelector(
      "[autofocus]:not(:disabled):not([type=hidden])"
    ) as HTMLElement;
    if (e) e.focus();
  }
  emit("shown", ...arguments);
  if (!props.shown) return;
  const shown = props.shown as (...args: unknown[]) => void;
  shown(...arguments);
}
function showHandler() {
  isShow.value = true;
  emit("show", ...arguments);
  if (!props.show) return;
  const show = props.show as (...args: unknown[]) => void;
  show(...arguments);
}
function hideHandler() {
  isShow.value = false;
  emit("hide", ...arguments);
  if (!props.hide) return;
  const hide = props.hide as (...args: unknown[]) => void;
  hide(...arguments);
}
function hiddenHandler() {
  emit("hidden", ...arguments);
  if (!props.hide) return;
  const hidden = props.hidden as (...args: unknown[]) => void;
  hidden(...arguments);
}
function hidePreventedHandler(): boolean | void {
  emit("hidePrevented", ...arguments);
  if (!props.hidePrevented) return;
  const hidePrevented = props.hidePrevented as (
    ...args: unknown[]
  ) => void | boolean;
  return hidePrevented(...arguments);
}
function clear() {
  defines.hide();
}
if (!props.ignoreForceClear) {
  allClear.on("clear", clear);
  onUnmounted(() => {
    allClear.off("clear", clear);
  });
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["modal", props.class]),
    ref_key: "modal",
    ref: modal,
    tabindex: "-1",
    id: id.value,
    "aria-labelledby": `${id.value}Label`,
    "aria-hidden": !isFirstShow.value
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(["modal-dialog", props.modalClass])
    }, [
      _createElementVNode("div", {
        class: _normalizeClass(["modal-content", props.contentClass])
      }, [
        _createElementVNode("div", {
          class: _normalizeClass(["modal-header", props.headerClass])
        }, [
          _createElementVNode("h5", {
            class: "modal-title",
            id: `${id.value}Label`
          }, [
            _renderSlot(_ctx.$slots, "title", {
              hide: __props.hide,
              isShow: isShow.value
            }, () => [
              _createTextVNode(_toDisplayString(_unref(title)), 1)
            ])
          ], 8, _hoisted_2),
          _withDirectives(_createElementVNode("button", _hoisted_3, null, 512), [
            [_vShow, !props.disableClose]
          ])
        ], 2),
        _createElementVNode("div", {
          class: _normalizeClass(["modal-body", props.bodyClass])
        }, [
          _renderSlot(_ctx.$slots, "default", {
            hide: _unref(defines).hide,
            isShow: isShow.value
          })
        ], 2),
        _createElementVNode("div", {
          class: _normalizeClass(["modal-footer", props.footerClass])
        }, [
          _renderSlot(_ctx.$slots, "footer", {
            hide: _unref(defines).hide,
            isShow: isShow.value
          }, () => [
            _createElementVNode("button", _hoisted_4, _toDisplayString(_unref(t)("MODAL.CANCEL")), 1),
            _createElementVNode("button", {
              type: "button",
              class: "btn btn-primary",
              onClick: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(defines).hide && _unref(defines).hide(...args)))
            }, _toDisplayString(_unref(t)("MODAL.OK")), 1)
          ])
        ], 2)
      ], 2)
    ], 2)
  ], 10, _hoisted_1))
}
}

})